import { ComponentProps, KeyboardEvent, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import uuid from "react-uuid";
import { isEmpty } from "lodash-es";
import { Presentation, Users } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { createPowerpoint, deleteGroupAborted, generateGroupConversation, generateSlideStructure, getCustomGPTObj, getPresentationDownloadUrl, getPresentationStatus, getSasUrl, loadGroupConversation, marketingSearch, notifyUsageChanged, pollGroupConversation } from "@/api/api";
import { ChatMessage, Conversation, ConversationResponseEnvelope, CustomGPT, DEFAULT_PERSONALIZATION, ErrorResponsePayload, IndividualFileChat, PresentationAsset, SlideStructure, StreamErrorDetails } from "@/api/models";
import { chatModels } from "@/constants/models";
import { applyStreamMessage, buildStreamMessages, createStreamMessageAccumulator, getDialogueMessages, normalizeCompactionTimeline, normalizeResponseMessages, preserveCompactionEventsOnError, readNdjsonStream, shouldHideLoadingMessageForChunk, STREAM_MESSAGE_ROLE } from "@/utils/chatStream";
import { downloadUrl, getPresentationFilename } from "@/components/Library/libraryUtils";
import { getErrorDetails, getErrorSummary } from "@/utils/errorMessages";
import { handleCheckMarketingStatus, handleCheckPowerpointStatus, handleCheckSlideStructureStatus, openChat, setTitle, toggleHistoryPanel } from "@/utils/utils";
import { useFileUpload } from "@/hooks/useFileUpload";
import PromptInput from "@/components/PromptInput/PromptInput";
import Answer from "@/components/Answer/Answer";
import Message from "@/components/Message/Message";
import GroupParticipants from "@/components/GroupConversation/GroupParticipants";
import ChatScrollNav from "@/components/Chat/ChatScrollNav";
import UploadProgress from "@/components/Chat/UploadProgress";
import LoadingState from "@/components/Answer/LoadingState";
import ContextCompactionDivider from "@/components/Chat/ContextCompactionDivider";
import ConversationFilesPanel from "@/components/ConversationFilesPanel/ConversationFilesPanel";
import SlideStructurePreview from "@/components/Chat/SlideStructurePreview";
import PresentationAssetCard from "@/components/Chat/PresentationAssetCard";

const ERROR = "error";
const NOOP = () => {};

/**
 * Folds server presentation records into local state, returning the same array
 * reference when nothing changed so effects keyed on `presentations` settle.
 * Assets added by other participants are appended; optimistic local ones are kept.
 */
const mergePresentations = (current: PresentationAsset[], incoming: PresentationAsset[], conversationTitle?: string) => {
  if (!incoming.length) return current;
  const byId = new Map(incoming.map((asset) => [asset.id, asset]));
  let changed = false;
  const next = current.map((asset) => {
    const update = byId.get(asset.id);
    byId.delete(asset.id);
    if (!update || (update.status === asset.status && update.updatedAt === asset.updatedAt && update.error === asset.error)) return asset;
    changed = true;
    return { ...asset, ...update, filename: getPresentationFilename(conversationTitle, update.filename || asset.filename) };
  });
  const additions = [...byId.values()].map((asset) => ({ ...asset, filename: getPresentationFilename(conversationTitle, asset.filename) }));
  if (!changed && !additions.length) return current;
  return [...next, ...additions];
};

type MessageProcessStatus = ComponentProps<typeof Message>["processMessages"];
const NOT_RUNNING_STATUS = "Not Running" as MessageProcessStatus;
const PROCESSING_STATUS = "Processing" as MessageProcessStatus;
const DONE_STATUS = "Done" as MessageProcessStatus;

const GroupChat = () => {
  const appStateContext = useContext(AppStateContext);
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const handleToggleHistoryPanel = () => toggleHistoryPanel(appStateContext);

  /** Open a conversation-level file via its SAS URL. */
  const onOpenFile = async (filename: string) => {
    const fileName = (appStateContext?.state.currentChat?.id || '') + '/' + (filename || '');
    const file_sas_url = await getSasUrl(fileName, 'fileconv');
    if (file_sas_url?.blob_url) window.open(file_sas_url.blob_url, "_blank");
  };

  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const chatScrollContainer = useRef<HTMLDivElement | null>(null);
  const abortFuncs = useRef([] as AbortController[]);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastPollRef = useRef<string>("");
  const streamBaseMessagesRef = useRef<ChatMessage[]>([]);
  const streamMessagesRef = useRef<ChatMessage[]>([]);
  const streamAccumulatorRef = useRef(createStreamMessageAccumulator());
  const streamErrorDetailsRef = useRef<StreamErrorDetails | null>(null);
  const isLoadingRef = useRef(false);
  const truncationInProgressRef = useRef(false);
  const startPathRef = useRef(location.pathname);
  const pendingInitialScroll = useRef(false);
  const anchoredUserMessageId = useRef<string | null>(null);
  const prevIsLoadingRef = useRef(false);

  const { indexing, uploadProgress, isDragging, processUploads, resolveExistingContext } = useFileUpload();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [compactionStatus, setCompactionStatus] = useState<string | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(true);
  const [processMessages, setProcessMessages] = useState<MessageProcessStatus>(NOT_RUNNING_STATUS);
  const [aborted, setAborted] = useState(false);
  const [abortedFeedbackMessageIds, setAbortedFeedbackMessageIds] = useState<Set<string>>(() => new Set());
  const [isSelecting, setIsSelecting] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isQuerying, setIsQuerying] = useState(false);
  const [longConversation, setLongConversation] = useState(false);
  const [userMessageScrollSpacerHeight, setUserMessageScrollSpacerHeight] = useState(0);
  const [question, setQuestion] = useState("");
  const [imageSize, setImageSize] = useState("1024x1024");
  const [imageStyle, setImageStyle] = useState("");
  const [marketingSearchLevel, setMarketingSearchLevel] = useState("medium");
  const [presentations, setPresentations] = useState<PresentationAsset[]>([]);
  const [isCreatingPowerpoint, setIsCreatingPowerpoint] = useState(false);
  const [isGeneratingSlideStructure, setIsGeneratingSlideStructure] = useState(false);
  const [isRegeneratingSlides, setIsRegeneratingSlides] = useState(false);
  const [createAiImages, setCreateAiImages] = useState(false);
  const [slideStructureData, setSlideStructureData] = useState<SlideStructure | null>(null);
  // Presentations whose durable-status chain is running in this session; polling
  // them here as well would just double the requests.
  const [livePolledPresentationIds, setLivePolledPresentationIds] = useState<string[]>([]);

  useEffect(() => {
    setCompactionStatus(null);
  }, [conversationId]);

  const currentChat = appStateContext?.state.currentChat;
  const currentCustomGPT = appStateContext?.state.currentCustomGPT || null;
  const isImagePromptMode = appStateContext?.state.selectedTool === "image_generation";
  const dialogueMessages = getDialogueMessages(messages);
  const renderedMessages = dialogueMessages.filter((message) => message.role !== "developer");
  const renderedMessageIds = new Set(renderedMessages.map((message) => message.id));
  const timelineMessages = messages.filter((message) => message.role !== "developer" && (
    message.role !== STREAM_MESSAGE_ROLE.compaction || (!!message.anchorMessageId && renderedMessageIds.has(message.anchorMessageId))
  ));
  // While files are still going up, the newest message is the optimistic one whose
  // chips are not openable yet.
  const pendingFilesMessageId = indexing ? dialogueMessages[dialogueMessages.length - 1]?.id : undefined;
  const lastMessageId = dialogueMessages[dialogueMessages.length - 1]?.id;
  const presentationIsLast = presentations.some((asset) => asset.status !== "failed" && (!asset.parentMessageId || asset.parentMessageId === lastMessageId));
  const currentModelName = currentChat?.model?.name || appStateContext?.state.currentModel?.name || "Chat";
  const currentCustomGPTName = currentCustomGPT?.name || "";

  // Keep persisted presentation cards current after returning to a group chat.
  const pendingPresentationIds = presentations
    .filter((asset) => asset.status === "creating" && !livePolledPresentationIds.includes(asset.id))
    .map((asset) => asset.id)
    .join(",");
  useEffect(() => {
    const pendingIds = pendingPresentationIds ? pendingPresentationIds.split(",") : [];
    if (!pendingIds.length) return;

    const refreshStatuses = async () => {
      const refreshed = await Promise.all(pendingIds.map((id) => getPresentationStatus(id)));
      const assets = refreshed.filter((asset): asset is PresentationAsset => !!asset);
      if (!assets.length) return;
      setPresentations((current) => mergePresentations(current, assets, currentChatRef.current?.title));
    };

    void refreshStatuses();
    const interval = window.setInterval(() => void refreshStatuses(), 5000);
    return () => window.clearInterval(interval);
  }, [pendingPresentationIds]);

  // Refs so effect callbacks always have fresh values without being in dep arrays
  const appStateContextRef = useRef(appStateContext);
  useEffect(() => { appStateContextRef.current = appStateContext; });
  // Poll chains capture this at start-up and bail once it moves, so a job
  // started in one conversation can never write into the next one.
  const activeConversationIdRef = useRef(conversationId);
  useEffect(() => { activeConversationIdRef.current = conversationId; });
  const currentChatRef = useRef(currentChat);
  useEffect(() => { currentChatRef.current = currentChat; }, [currentChat]);

  // Close any open slide preview and clear in-flight flags when the conversation
  // changes; the polls themselves bail via activeConversationIdRef.
  useEffect(() => {
    setSlideStructureData(null);
    setIsGeneratingSlideStructure(false);
    setIsRegeneratingSlides(false);
    setIsCreatingPowerpoint(false);
    setLivePolledPresentationIds([]);
  }, [conversationId]);

  // ── Load conversation on mount / when ID changes ────────────────────────────
  useEffect(() => {
    // Local to this effect run: flipped by cleanup on unmount / conversation
    // change so async work that resolves afterwards can't dispatch stale state.
    let cancelled = false;
    const ctx = appStateContextRef.current;
    if (!conversationId) {
      setIsLoadingConversation(false);
      ctx?.dispatch({ type: "SET_INDIVIDUAL_FILE_CHAT", payload: null });
      return;
    }

    const load = async () => {
      // Saved conversations must not inherit the spacer used to anchor a new prompt.
      setUserMessageScrollSpacerHeight(0);
      pendingInitialScroll.current = false;
      anchoredUserMessageId.current = null;
      setIsLoadingConversation(true);
      try {
        const result = await loadGroupConversation(conversationId);
        if (cancelled) return;
        // Re-read ref after await so we have the latest context
        const latestCtx = appStateContextRef.current;
        if (result) {
          const conv: Conversation = { ...result.conversation, messages: result.messages };
          let conversationCustomGPT: CustomGPT | null = null;
          if (!isEmpty(conv.customGPTID)) {
            conversationCustomGPT = await getCustomGPTObj(conv.customGPTID);
            if (cancelled) return;
          }
          if (latestCtx) openChat(latestCtx, conv, false, conversationCustomGPT);
          latestCtx?.dispatch({ type: "UPDATE_GROUP_CHAT", payload: result.conversation });
          setMessages(result.messages);
          if (latestCtx) setTitle(latestCtx, result.conversation.title);
          lastPollRef.current = result.nextCursor || result.conversation.date || "";
          streamBaseMessagesRef.current = result.messages;
          streamMessagesRef.current = result.messages;
          streamAccumulatorRef.current = createStreamMessageAccumulator();
          setPresentations(mergePresentations([], conv.presentations || [], conv.title));

          // Restore file context so file-backed group chats behave correctly
          if (conv.files) {
            // Dedupe: records written before the group-generate fix re-appended the same
            // filenames on every turn, and re-hydrating those duplicates would both render
            // hundreds of chips and feed them straight back to the backend.
            const filenames = Array.from(new Set(conv.files.split('|').filter(Boolean)));
            const individualFileChat: IndividualFileChat[] = filenames.map(f => ({
              id: conv.id,
              filename: f,
              imageEditing: false,
            }));
            latestCtx?.dispatch({ type: 'SET_INDIVIDUAL_FILE_CHAT', payload: individualFileChat });
          } else if (conv.filename) {
            latestCtx?.dispatch({ type: 'SET_INDIVIDUAL_FILE_CHAT', payload: [{ id: conv.id, filename: conv.filename, imageEditing: false }] });
          } else {
            latestCtx?.dispatch({ type: 'SET_INDIVIDUAL_FILE_CHAT', payload: null });
          }

          // Jump straight to the latest message — no animation when opening a chat
          setTimeout(() => {
            if (cancelled) return;
            const scrollContainer = chatScrollContainer.current;
            if (scrollContainer) scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }, 100);
        } else {
          navigate("/");
        }
      } finally {
        setIsLoadingConversation(false);
      }
    };

    load();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // ── Poll for new messages every 5 s ────────────────────────────────────────
  useEffect(() => {
    if (!conversationId) return;

    // Local to this effect run — see load effect above.
    let cancelled = false;

    pollingRef.current = setInterval(async () => {
      if (isLoadingRef.current || truncationInProgressRef.current) return;
      try {
        const since = lastPollRef.current;
        const poll = await pollGroupConversation(conversationId, since);
        if (cancelled) return;
        const ctx = appStateContextRef.current;
        const chat = currentChatRef.current;
        if (poll === "access_denied") {
          clearInterval(pollingRef.current!);
          ctx?.dispatch({ type: "DELETE_GROUP_CHAT", payload: conversationId });
          ctx?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
          navigate("/");
          return;
        }
        if (poll) {
          lastPollRef.current = poll.nextCursor || since;
          if (chat?.id) {
            ctx?.dispatch({
              type: "UPDATE_GROUP_CHAT",
              payload: {
                ...chat,
                date: poll.updatedAt || chat.date,
                participants: poll.participants || chat.participants || [],
              },
            });
          }
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const additions = poll.messages.filter(m => !existingIds.has(m.id));
            const updateMap = new Map(poll.messages.filter(m => existingIds.has(m.id)).map(m => [m.id, m]));

            if (additions.length === 0 && updateMap.size === 0) return prev;

            const nextMessages = normalizeCompactionTimeline([
              ...prev.map(m => updateMap.has(m.id) ? { ...m, ...updateMap.get(m.id) } : m),
              ...additions,
            ]);
            const latestChat = currentChatRef.current;
            if (latestChat?.id === conversationId) {
              appStateContextRef.current?.dispatch({
                type: "UPDATE_CURRENT_CHAT",
                payload: { ...latestChat, messages: nextMessages },
              });
            }
            return nextMessages;
          });
          setPresentations(prev => mergePresentations(prev, poll.presentations, currentChatRef.current?.title));
        }
      } catch {
        // silent
      }
    }, 5000);

    return () => {
      cancelled = true;
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // ── Long conversation warning ──────────────────────────────────────────────
  useEffect(() => {
    const userMessages = messages.filter(m => m.role === "user");
    setLongConversation(userMessages.length >= 25);
  }, [messages]);

  // RAF-based smooth scroll — works regardless of the container's overflow CSS
  const smoothScrollTo = (el: HTMLElement, target: number, duration = 300) => {
    const start = el.scrollTop;
    const diff = target - start;
    if (diff === 0) return;
    const startTime = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      el.scrollTop = start + diff * eased;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  // ── New message mode — initial scroll on send ──────────────────────────────
  // Prompt > 75% viewport height → bottom of prompt at top of view (response visible)
  // Prompt ≤ 75% viewport height → top of prompt 16px from top of view
  // Grows a bottom spacer until targetScrollTop is reachable, then scrolls once.
  useLayoutEffect(() => {
    if (!pendingInitialScroll.current || !anchoredUserMessageId.current) return;

    const scrollContainer = chatScrollContainer.current;
    if (!scrollContainer) return;

    const msgEl = scrollContainer.querySelector(
      `[data-user-message-id="${anchoredUserMessageId.current}"]`
    ) as HTMLElement | null;
    if (!msgEl) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const msgRect = msgEl.getBoundingClientRect();
    const containerHeight = scrollContainer.clientHeight;
    const SCROLL_PADDING = 16;

    const msgTopAbs = scrollContainer.scrollTop + (msgRect.top - containerRect.top);
    const msgBottomAbs = scrollContainer.scrollTop + (msgRect.bottom - containerRect.top);

    const targetScrollTop = msgRect.height > containerHeight * 0.75
      ? Math.max(0, msgBottomAbs)
      : Math.max(0, msgTopAbs - SCROLL_PADDING);

    const naturalMax = scrollContainer.scrollHeight - userMessageScrollSpacerHeight - containerHeight;
    const requiredSpacer = Math.max(0, targetScrollTop - naturalMax);

    if (Math.abs(requiredSpacer - userMessageScrollSpacerHeight) > 1) {
      setUserMessageScrollSpacerHeight(requiredSpacer);
    } else {
      smoothScrollTo(scrollContainer, targetScrollTop);
      pendingInitialScroll.current = false;
    }
  }, [messages, userMessageScrollSpacerHeight]);

  // ── New message mode — spacer cleanup after generation ────────────────────
  // Trims spacer to only what's needed to keep scrollTop valid. Because neededSpacer
  // is the exact minimum, the browser won't clamp scrollTop — no compensation needed.
  useLayoutEffect(() => {
    if (!prevIsLoadingRef.current || isLoading) {
      prevIsLoadingRef.current = isLoading;
      return;
    }
    prevIsLoadingRef.current = false;

    anchoredUserMessageId.current = null;
    pendingInitialScroll.current = false;

    if (userMessageScrollSpacerHeight === 0) return;

    const scrollContainer = chatScrollContainer.current;
    if (!scrollContainer) return;

    const naturalScrollHeight = scrollContainer.scrollHeight - userMessageScrollSpacerHeight;
    const naturalMax = naturalScrollHeight - scrollContainer.clientHeight;
    const neededSpacer = Math.max(0, scrollContainer.scrollTop - naturalMax);

    if (neededSpacer < userMessageScrollSpacerHeight - 1) {
      setUserMessageScrollSpacerHeight(neededSpacer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const removeAbortController = (abortController: AbortController) => {
    abortFuncs.current = abortFuncs.current.filter((controller) => controller !== abortController);
  };

  const syncBaseMessages = (baseMessages: ChatMessage[]) => {
    streamBaseMessagesRef.current = baseMessages;
    streamMessagesRef.current = baseMessages;
    setMessages(baseMessages);
  };

  const buildErrorChatMessage = (
    message: string,
    errorPayload?: Partial<ErrorResponsePayload>,
    details?: StreamErrorDetails | null,
  ): ChatMessage => {
    const normalizedDetails = getErrorDetails(message, details || errorPayload?.error_details);
    const errorDetails = errorPayload?.error_code && !normalizedDetails.error_code
      ? { ...normalizedDetails, error_code: errorPayload.error_code }
      : normalizedDetails;
    return {
      id: uuid(),
      role: ERROR,
      content: getErrorSummary(message, errorDetails),
      date: new Date().toISOString(),
      error_code: errorPayload?.error_code,
      error_type: errorPayload?.error_type,
      error_details: errorDetails,
    };
  };

  const getFinalizedMessages = () => {
    if (streamMessagesRef.current.length > 0) {
      return [...streamMessagesRef.current];
    }

    return buildStreamMessages(
      streamBaseMessagesRef.current,
      streamAccumulatorRef.current,
    );
  };

  const persistGroupConversation = (conversation: Conversation, finalizedMessages: ChatMessage[]) => {
    const finalizedConversation = { ...conversation, messages: finalizedMessages };
    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: finalizedConversation });
    appStateContext?.dispatch({ type: "UPDATE_GROUP_CHAT", payload: finalizedConversation });

    if (isOnGenerationTarget()) setMessages(finalizedMessages);

    return finalizedConversation;
  };

  const isOnGenerationTarget = () => location.pathname === startPathRef.current;

  const scrollConversationToBottom = () => {
    window.requestAnimationFrame(() => {
      const scrollContainer = chatScrollContainer.current;
      if (scrollContainer) scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: "smooth" });
    });
  };

  const pollPowerpointStatus = async (statusUrl: string, presentationId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    const pollConversationId = activeConversationIdRef.current;
    const releaseLivePoll = () => setLivePolledPresentationIds((ids) => ids.filter((id) => id !== presentationId));
    setLivePolledPresentationIds((ids) => [...ids, presentationId]);

    const finish = (status: PresentationAsset["status"], error: string) => {
      releaseLivePoll();
      setIsCreatingPowerpoint(false);
      setPresentations((current) => current.map((asset) => asset.id === presentationId
        ? { ...asset, status, error }
        : asset));
    };

    const poll = async () => {
      // The user moved on; the record is still updated server-side and the group
      // poll will surface it when they come back.
      if (activeConversationIdRef.current !== pollConversationId) {
        releaseLivePoll();
        return;
      }
      attempts += 1;
      const result = await handleCheckPowerpointStatus(statusUrl, presentationId);
      if (result.complete) {
        finish(result.success ? "ready" : "failed", result.success ? "" : "Presentation generation failed");
      } else if (attempts < maxAttempts) {
        window.setTimeout(poll, 5000);
      } else {
        finish("failed", "Presentation generation timed out");
      }
    };
    void poll();
  };

  const pollSlideStructureStatus = async (statusUrl: string) => {
    const maxAttempts = 48;
    let attempts = 0;
    const pollConversationId = activeConversationIdRef.current;
    const poll = async () => {
      // Dropping the result is correct here: a structure belongs to the
      // conversation it was generated from, and that one is no longer open.
      if (activeConversationIdRef.current !== pollConversationId) return;
      attempts += 1;
      const result = await handleCheckSlideStructureStatus(statusUrl);
      if (result.complete) {
        setIsGeneratingSlideStructure(false);
        setIsRegeneratingSlides(false);
        if (result.success && result.slideJson) {
          setSlideStructureData(result.slideJson);
          scrollConversationToBottom();
        }
      } else if (attempts < maxAttempts) {
        window.setTimeout(poll, 5000);
      } else {
        setIsGeneratingSlideStructure(false);
        setIsRegeneratingSlides(false);
      }
    };
    void poll();
  };

  const onGenerateSlideStructure = async (changeRequest = "") => {
    if (!conversationId || isCreatingPowerpoint) return;
    scrollConversationToBottom();
    if (slideStructureData) {
      setIsRegeneratingSlides(true);
    } else {
      setIsGeneratingSlideStructure(true);
      setCreateAiImages(false);
    }
    try {
      const response = await generateSlideStructure(conversationId, changeRequest, slideStructureData);
      if (response.ok) {
        const data = await response.json();
        if (data.statusQueryGetUri) pollSlideStructureStatus(data.statusQueryGetUri);
        else {
          setIsGeneratingSlideStructure(false);
          setIsRegeneratingSlides(false);
        }
      } else {
        setIsGeneratingSlideStructure(false);
        setIsRegeneratingSlides(false);
      }
    } catch {
      setIsGeneratingSlideStructure(false);
      setIsRegeneratingSlides(false);
    }
  };

  const onConfirmSlideStructure = async (approvedJson: SlideStructure) => {
    if (!conversationId) return;
    const pendingPresentationId = `starting-${uuid()}`;
    const parentMessageId = [...messages].reverse().find((message) => message.role === "assistant")?.id || "";
    const pendingPresentation: PresentationAsset = {
      id: pendingPresentationId,
      conversationId,
      parentMessageId,
      status: "starting",
      filename: getPresentationFilename(currentChat?.title),
      blobName: "",
      createdAt: new Date().toISOString(),
    };
    setSlideStructureData(null);
    setIsCreatingPowerpoint(true);
    setPresentations((current) => [...current, pendingPresentation]);
    scrollConversationToBottom();

    // Tracks whichever id the card currently carries, so a late failure marks
    // the real record once the optimistic one has been swapped out.
    let assetId = pendingPresentationId;
    try {
      const response = await createPowerpoint(conversationId, createAiImages, approvedJson);
      if (!response.ok) throw new Error("Presentation generation could not be started");
      const data = await response.json();
      const presentation = data.presentation as PresentationAsset | undefined;
      if (!presentation) throw new Error("Presentation record was not returned");
      // A poll may already have delivered the server record; keep a single card.
      // The server resolves the parent from stored ids, which can differ from the ids on screen mid-session.
      setPresentations((current) => current
        .filter((asset) => asset.id !== presentation.id)
        .map((asset) => asset.id === pendingPresentationId
          ? { ...presentation, parentMessageId: messages.some((message) => message.id === presentation.parentMessageId) ? presentation.parentMessageId : parentMessageId, filename: getPresentationFilename(currentChat?.title, presentation.filename) }
          : asset));
      assetId = presentation.id;
      if (!data.statusQueryGetUri) throw new Error("Presentation status URL was not returned");
      pollPowerpointStatus(data.statusQueryGetUri, presentation.id);
    } catch {
      setIsCreatingPowerpoint(false);
      setPresentations((current) => current.map((asset) => asset.id === assetId
        ? { ...asset, status: "failed", error: "Presentation generation could not be started" }
        : asset));
    }
  };

  // ── Send message ───────────────────────────────────────────────────────────
  const sendPrompt = async (prompt: string, continuation: boolean = false, retainedMessages?: ChatMessage[]) => {
    if (!prompt.trim() || isLoading || truncationInProgressRef.current || !conversationId) return;

    // Clean up ghost user message left by a previous abort
    const baseMessages = retainedMessages ?? messages;
    const baseDialogue = getDialogueMessages(baseMessages);
    const latestDialogueMessage = baseDialogue[baseDialogue.length - 1];
    const activeMessages =
      aborted && latestDialogueMessage?.role === "user"
        ? baseMessages.filter(message => message.id !== latestDialogueMessage.id && !(message.role === STREAM_MESSAGE_ROLE.compaction && message.anchorMessageId === latestDialogueMessage.id))
        : baseMessages;

    if (activeMessages !== messages) {
      setMessages(activeMessages);
    }
    setAborted(false);
    streamErrorDetailsRef.current = null;

    if (appStateContext?.state.selectedTool === "ms_office_data") setIsQuerying(true);

    const abortController = new AbortController();
    abortFuncs.current.push(abortController);
    const senderName =
      appStateContext?.state.currentUser?.name ||
      currentChat?.participants?.find((participant) => participant.userId === appStateContext?.state.currentUser?.id)?.name ||
      currentChat?.ownerName ||
      "";

    const userMessage: ChatMessage = {
      id: uuid(),
      role: "user",
      content: prompt,
      context: "",
      date: new Date().toISOString(),
      userId: appStateContext?.state.currentUser?.id || "",
      senderName,
    };

    // ── File / image upload handling ────────────────────────────────────────

    // Draw the message before the upload starts. Uploading and indexing can run for
    // minutes, and until this lands the transcript gives no sign the send happened.
    // Attached filenames are known client-side, so the chips can show immediately.
    const pendingFileList = appStateContext?.state.fileUpload?.current?.files;
    if (pendingFileList?.length) {
      userMessage.files = Array.from<File>(pendingFileList).map((f) => f.name).join('|');
    }
    pendingInitialScroll.current = true;
    anchoredUserMessageId.current = userMessage.id;
    setMessages([...activeMessages, userMessage]);

    const uploadResult = await processUploads(
      conversationId,
      userMessage,
      getDialogueMessages(activeMessages).length,
    );
    if (uploadResult.error) {
      // Keep the prompt on screen beside the error rather than flashing it away.
      // Nothing indexed, so drop the optimistic chips.
      userMessage.files = '';
      const errorChatMsg = buildErrorChatMessage(
        uploadResult.errorMessage || "There was an error uploading the file, please refresh the page and try again.",
        undefined,
        uploadResult.errorDetails,
      );
      setMessages([...activeMessages, userMessage, errorChatMsg]);
      removeAbortController(abortController);
      return;
    }
    const { initialFile, addedFile, directFiles, effectiveModel } = uploadResult;
    userMessage.model = effectiveModel.deployment;
    const { imageBlobURL } = uploadResult;
    let { filesStr, filename } = uploadResult;
    const existing = resolveExistingContext(initialFile, addedFile);
    if (!filesStr) filesStr = existing.filesStr;
    if (!filename) filename = existing.filename;

    // ── Marketing high search pre-processing ────────────────────────────────
    let marketingGuid = "";
    if (!isEmpty(currentCustomGPT) && currentCustomGPT.id === "e7e6d46d-dd49-4a08-b02a-4499de9d540a" && marketingSearchLevel === "high") {
      const search_resp = await marketingSearch(prompt);
      if (search_resp && search_resp.status === 200) {
        const data = await search_resp.json();
        marketingGuid = data["res"]["guid"] || "";
        if (!isEmpty(data["res"]["guid"])) {
          let complete = false;
          while (!complete) {
            complete = await handleCheckMarketingStatus(data["res"]["guid"]);
            if (!complete) {
              await new Promise(r => setTimeout(r, 15000));
            }
          }
        }
      }
    }

    // ── Build messages array and send ───────────────────────────────────────

    startPathRef.current = location.pathname;

    setIsLoading(true);
    isLoadingRef.current = true;
    setShowLoadingMessage(true);
    setQuestion("");

    const updatedMessages = [...activeMessages, userMessage];
    let discardPartialOnError = false;
    syncBaseMessages(updatedMessages);

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const request = { messages: getDialogueMessages(updatedMessages).filter(m => m.role !== ERROR) };

      const response = await generateGroupConversation(
        request,
        effectiveModel,
        currentCustomGPT,
        appStateContext?.state.currentAgent || null,
        abortController.signal,
        conversationId,
        timezone,
        appStateContext?.state.selectedTool || "",
        filesStr,
        filename,
        addedFile,
        initialFile,
        directFiles,
        imageBlobURL,
        imageSize,
        imageStyle,
        marketingSearchLevel,
        marketingGuid,
        continuation,
        appStateContext?.state.personalization || DEFAULT_PERSONALIZATION
      );

      if (!response?.ok) {
        const responseJson = await response.json() as ErrorResponsePayload;
        const errorMsg = responseJson.error || "An error occurred. Please try again.";
        setMessages([...updatedMessages, buildErrorChatMessage(errorMsg, responseJson, responseJson.error_details)]);
        return;
      }

      if (response.body) {
        let result = {} as ConversationResponseEnvelope;
        let streamDone = false;
        let streamPersistenceConfirmed = false;
        // See Chat: only the NDJSON stream promises a terminal marker; a single
        // jsonify'd envelope legitimately has none.
        const isNdjsonStream = (response.headers.get("content-type") || "").includes("json-lines");
        streamAccumulatorRef.current = createStreamMessageAccumulator();
        await readNdjsonStream(response.body, async (parsedResult) => {
          if ((parsedResult as { discard_partial_on_error?: boolean }).discard_partial_on_error === true) {
            discardPartialOnError = true;
            return;
          }

          // Provider output finishes before the server confirms persistence.
          // Stop the generating animation here, but keep the current answer
          // feedback-disabled until the persistence acknowledgment arrives.
          if ((parsedResult as any).__stream_done__) {
            streamDone = true;
            if (isOnGenerationTarget()) {
              setCompactionStatus(null);
              setProcessMessages(DONE_STATUS);
              setShowLoadingMessage(false);
            }
            return;
          }

          const persistenceResult = (parsedResult as any).__messages_persisted__;
          if (persistenceResult !== undefined) {
            if (persistenceResult?.ok !== true) {
              throw new Error("The response could not be saved. Please try again.");
            }
            streamPersistenceConfirmed = true;
            if (isOnGenerationTarget()) setIsLoading(false);
            return;
          }

          if ((parsedResult as ChatMessage).role === "status") {
            applyStreamMessage(streamAccumulatorRef.current, parsedResult as ChatMessage, {
              setCompactionStatus: (status) => {
                if (isOnGenerationTarget()) setCompactionStatus(status);
              },
            });
            return;
          }

          // Backend hit an unrecoverable error after the HTTP response already
          // started streaming (e.g. upstream model overloaded mid-stream). The
          // 200 is already committed, so the only signal is this marker — throw
          // so the outer catch renders the standard error message instead of
          // silently stopping the spinner with no response.
          if ((parsedResult as any).__stream_error__) {
            if (isOnGenerationTarget()) setCompactionStatus(null);
            const errorChunk = parsedResult as { error?: unknown; error_details?: StreamErrorDetails };
            const backendMsg = errorChunk.error;
            // Stash diagnostics before unwinding — `throw` only carries a string,
            // and the catch below is what actually builds the error message.
            streamErrorDetailsRef.current = errorChunk.error_details || null;
            throw new Error(
              typeof backendMsg === "string" && backendMsg.trim()
                ? backendMsg
                : "There was an error generating a response. Please try again."
            );
          }

          result = parsedResult as ConversationResponseEnvelope;

          if (result.error) {
            throw new Error(typeof result.error === "string" ? result.error : result.error?.message);
          }

          if (!result.choices?.length && (result as any).role !== STREAM_MESSAGE_ROLE.compaction) {
            if (result.url) {
              applyStreamMessage(streamAccumulatorRef.current, {
                id: result.id || uuid(),
                date: new Date().toISOString(),
                role: "image",
                content: result.url,
              }, {
                setIsSelecting,
                setIsSummarizing,
                setIsQuerying,
                setShowLoadingMessage,
                setCompactionStatus: (status) => {
                  if (isOnGenerationTarget()) setCompactionStatus(status);
                },
              });

              const streamedMessages = buildStreamMessages(
                streamBaseMessagesRef.current,
                streamAccumulatorRef.current,
              );
              streamMessagesRef.current = streamedMessages;
              if (isOnGenerationTarget()) {
                setMessages(streamedMessages);
                setShowLoadingMessage(false);
                if (!streamDone) setProcessMessages(PROCESSING_STATUS);
              }
            }
            return;
          }

          const normalizedMessages = normalizeResponseMessages(result);
          normalizedMessages.forEach((msg: ChatMessage) => {
            applyStreamMessage(streamAccumulatorRef.current, msg, {
              setIsSelecting,
              setIsSummarizing,
              setIsQuerying,
              setShowLoadingMessage,
              setCompactionStatus: (status) => {
                if (isOnGenerationTarget()) setCompactionStatus(status);
              },
            });
          });

          const streamedMessages = buildStreamMessages(
            streamBaseMessagesRef.current,
            streamAccumulatorRef.current,
          );
          streamMessagesRef.current = streamedMessages;
          if (isOnGenerationTarget()) setMessages(streamedMessages);

          if (shouldHideLoadingMessageForChunk(normalizedMessages) && isOnGenerationTarget()) {
            setShowLoadingMessage(false);
            if (!streamDone) setProcessMessages(PROCESSING_STATUS);
          }
        // _stream_group_response always closes with a terminal marker, so a stream
        // that ends without one was cut short and must surface as an error.
        }, { requireTerminalMarker: isNdjsonStream });

        if (isNdjsonStream && !streamPersistenceConfirmed) {
          throw new Error("The response could not be confirmed as saved. Please retry.");
        }

        const finalMessages = getFinalizedMessages();
        const resultConversation: Conversation = { ...(currentChatRef.current as Conversation), model: effectiveModel, messages: finalMessages };
        persistGroupConversation(resultConversation, finalMessages);
        if (isOnGenerationTarget()) setProcessMessages(DONE_STATUS);
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        const errorMessage =
          e instanceof Error && !isEmpty(e.message)
            ? e.message
            : "An error occurred. Please try again.";

        // Preserve ordinary partial answers, but roll back provisional tabular
        // answers because the backend discards them when the handoff fails.
        const streamedMessages = getFinalizedMessages();
        // Content-filtered output is also discarded by the backend.
        const hasPartialAnswer = !discardPartialOnError && (streamErrorDetailsRef.current as StreamErrorDetails | null)?.reason !== "content_filter" && streamedMessages
          .slice(updatedMessages.length)
          .some((msg) => msg.role === "assistant" && typeof msg.content === "string" && !isEmpty(msg.content.trim()));
        const messagesWithError = [
          ...(hasPartialAnswer ? streamedMessages : preserveCompactionEventsOnError(updatedMessages, streamedMessages)),
          buildErrorChatMessage(errorMessage, undefined, streamErrorDetailsRef.current),
        ];

        if (isOnGenerationTarget()) setMessages(messagesWithError);
      }
    } finally {
      if (isOnGenerationTarget()) {
        setIsLoading(false);
        isLoadingRef.current = false;
        setShowLoadingMessage(false);
        setCompactionStatus(null);
        setProcessMessages(NOT_RUNNING_STATUS);
        setIsSelecting(false);
        setIsSummarizing(false);
        setIsQuerying(false);
        notifyUsageChanged();
      }
      removeAbortController(abortController);
      startPathRef.current = '';
    }
  };

  const truncateConversationFrom = async (messageId: string) => {
    const response = conversationId ? await deleteGroupAborted(conversationId, messageId) : null;
    if (response?.ok) return true;
    setMessages(current => [...current.filter(message => message.role !== ERROR), buildErrorChatMessage(
      "Unable to update conversation history. Please try again before sending another message.",
    )]);
    return false;
  };

  const stopGenerating = async () => {
    if (truncationInProgressRef.current) return;
    const partialFeedbackMessageIds = getFinalizedMessages()
      .slice(streamBaseMessagesRef.current.length)
      .filter((message) => (message.role === "assistant" || message.role === "image") && typeof message.content === "string" && message.content.trim())
      .map((message) => message.id);
    if (partialFeedbackMessageIds.length > 0) {
      setAbortedFeedbackMessageIds((current) => new Set([...current, ...partialFeedbackMessageIds]));
    }

    setAborted(true);
    abortFuncs.current.forEach(a => a.abort("user aborted"));
    setShowLoadingMessage(false);
    setIsLoading(false);
    isLoadingRef.current = false;

    if (!conversationId || dialogueMessages.length === 0) return;
    const prompt = dialogueMessages.findLast(message => message.role === "user");
    if (prompt) {
      truncationInProgressRef.current = true;
      try {
        setAborted(await truncateConversationFrom(prompt.id));
      } finally {
        truncationInProgressRef.current = false;
      }
    }
  };

  const retryLatestUserMessage = async () => {
    if (isLoading || truncationInProgressRef.current || !conversationId) return;
    const index = dialogueMessages.findLastIndex(message => message.role === "user");
    const prompt = dialogueMessages[index];
    if (!prompt) return;

    setResubmitting(true);
    truncationInProgressRef.current = true;
    try {
      if (!await truncateConversationFrom(prompt.id)) return;
      const retainedDialogueIds = new Set(dialogueMessages.slice(0, index).map(message => message.id));
      const retainedMessages = messages.filter(message => message.role === STREAM_MESSAGE_ROLE.compaction
        ? !!message.anchorMessageId && retainedDialogueIds.has(message.anchorMessageId)
        : retainedDialogueIds.has(message.id));
      setMessages(retainedMessages);
      truncationInProgressRef.current = false;
      await sendPrompt(prompt.content, false, retainedMessages);
    } finally {
      truncationInProgressRef.current = false;
      setResubmitting(false);
    }
  };

  const submitContinueMessage = () => {
    if (!isLoading) void sendPrompt("*continue*", true);
  };

  const handleEditEnterPress = (event: KeyboardEvent<Element>) => {
    if (event.key === "Enter" && !event.shiftKey) event.preventDefault();
  };

  if (isLoadingConversation) {
    return (
      <div className={`content flex flex-col h-full ${appStateContext?.state.sidepanelOpen ? "shifted-left" : ""}`}>
        <div className="child-div flex flex-col w-full h-full">
          <div className="child-div-parent flex flex-1 flex-col items-center justify-center scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
            <div className="loading-icon w-24 h-24"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`content flex min-h-0 flex-col h-full ${appStateContext?.state.sidepanelOpen ? "shifted-left" : ""}`}
      onDragOver={e => e.preventDefault()}
    >
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-base-100/80 backdrop-blur-sm pointer-events-none">
          <div className="border-2 border-dashed border-primary rounded-xl p-10 text-primary text-xl font-semibold">
            Drop files here
          </div>
        </div>
      )}

      {/* Header */}
      <div className="shrink-0 flex items-center justify-between gap-2 border-b border-secondary px-1">
        <div className="flex min-w-0 items-center">
          {!appStateContext?.state.sidepanelOpen && (
            <button
              type="button"
              className={`btn btn-ghost hover:bg-transparent dark:text-white ${screen.width <= 700 ? "px-1" : "px-2 mx-1"}`}
              onClick={handleToggleHistoryPanel}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-xl-heavy"><path fillRule="evenodd" clipRule="evenodd" d="M8.85719 3H15.1428C16.2266 2.99999 17.1007 2.99998 17.8086 3.05782C18.5375 3.11737 19.1777 3.24318 19.77 3.54497C20.7108 4.02433 21.4757 4.78924 21.955 5.73005C22.2568 6.32234 22.3826 6.96253 22.4422 7.69138C22.5 8.39925 22.5 9.27339 22.5 10.3572V13.6428C22.5 14.7266 22.5 15.6008 22.4422 16.3086C22.3826 17.0375 22.2568 17.6777 21.955 18.27C21.4757 19.2108 20.7108 19.9757 19.77 20.455C19.1777 20.7568 18.5375 20.8826 17.8086 20.9422C17.1008 21 16.2266 21 15.1428 21H8.85717C7.77339 21 6.89925 21 6.19138 20.9422C5.46253 20.8826 4.82234 20.7568 4.23005 20.455C3.28924 19.9757 2.52433 19.2108 2.04497 18.27C1.74318 17.6777 1.61737 17.0375 1.55782 16.3086C1.49998 15.6007 1.49999 14.7266 1.5 13.6428V10.3572C1.49999 9.27341 1.49998 8.39926 1.55782 7.69138C1.61737 6.96253 1.74318 6.32234 2.04497 5.73005C2.52433 4.78924 3.28924 4.02433 4.23005 3.54497C4.82234 3.24318 5.46253 3.11737 6.19138 3.05782C6.89926 2.99998 7.77341 2.99999 8.85719 3Z" fill="currentColor"></path></svg>
            </button>
          )}
          <button type="button" className="btn btn-ghost hover:bg-transparent cursor-default px-0 mx-0"></button>
          <div className="flex min-w-0 items-center gap-2 rounded-lg px-2 py-2">
            <Users size={18} className="shrink-0" />
            <div className="min-w-0">
              <div className="truncate text-lg font-semibold select-none dark:text-white">
                {currentChat?.title || "Group Chat"}
              </div>
              <div className="truncate text-[11px] text-accent">
                {currentCustomGPTName || ``}
              </div>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-1 pe-2 py-2">
          <button
            type="button"
            className={`btn btn-ghost btn-sm gap-2 ${(isCreatingPowerpoint || isGeneratingSlideStructure || presentationIsLast) ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={() => { if (!isCreatingPowerpoint && !isGeneratingSlideStructure && !presentationIsLast) onGenerateSlideStructure(); }}
            disabled={isCreatingPowerpoint || isGeneratingSlideStructure || presentationIsLast}
            title="Create PowerPoint"
          >
            <Presentation size={16} />
            <span className="hidden sm:inline">{isGeneratingSlideStructure ? "Creating" : "Create"} PowerPoint</span>
          </button>
          {currentChat && <GroupParticipants conversation={currentChat} />}
        </div>
      </div>

      <div className="flex flex-1 min-h-0 justify-center">
        <div className="mx-auto flex min-h-0 w-full flex-col">
          <div className="main-div flex min-h-0 flex-1 flex-col items-center transitioned">
            <div className="child-div gg flex min-h-0 flex-1 flex-col w-full relative">
              <div ref={chatScrollContainer} className="child-div-parent flex min-h-0 flex-1 flex-col w-full items-center scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
                <div className="max-w-[1024px] w-full">
                  {timelineMessages.map((message, index) => message.role === STREAM_MESSAGE_ROLE.compaction ? (
                    <div key={message.id || `compaction-${index}`}><ContextCompactionDivider /></div>
                  ) : (
                    <div key={message.id || index}>
                      <Message
                      key={message.id || index}
                      messages={renderedMessages}
                      message={message}
                      index={renderedMessages.indexOf(message)}
                      processMessages={processMessages}
                      resubmitUserMessage={retryLatestUserMessage}
                      convDisabled={false}
                      editUserMessage=""
                      setEditUserMessage={NOOP}
                      setEditUserMessageContent={NOOP}
                      onEnterPress={handleEditEnterPress}
                      resubmitting={resubmitting}
                      resubmitEditMessage={NOOP}
                      cancelEditMessage={NOOP}
                      isSelecting={isSelecting}
                      isSummarizing={isSummarizing}
                      isQuerying={isQuerying}
                      submitContinueMessage={submitContinueMessage}
                      isLoading={isLoading}
                      feedbackDisabled={abortedFeedbackMessageIds.has(message.id)}
                      filesPending={!!pendingFilesMessageId && message.id === pendingFilesMessageId}
                      />
                      {presentations.filter((asset) => asset.parentMessageId === message.id).map((asset) => (
                        <PresentationAssetCard
                          key={asset.id}
                          asset={asset}
                          onDownload={async (item) => {
                            const url = await getPresentationDownloadUrl(item.id);
                            if (url) void downloadUrl(url, item.filename);
                          }}
                        />
                      ))}
                    </div>
                  ))}
                  {presentations.filter((asset) => !asset.parentMessageId).map((asset) => (
                    <PresentationAssetCard
                      key={asset.id}
                      asset={asset}
                      onDownload={async (item) => {
                        const url = await getPresentationDownloadUrl(item.id);
                        if (url) void downloadUrl(url, item.filename);
                      }}
                    />
                  ))}
                  {isGeneratingSlideStructure && !slideStructureData && (
                    <div className="px-1 my-6">
                      <LoadingState label="Generating slide structure" />
                    </div>
                  )}
                  {slideStructureData && (
                    <SlideStructurePreview
                      slideData={slideStructureData}
                      isRegenerating={isRegeneratingSlides}
                      createAiImages={createAiImages}
                      onSetCreateAiImages={setCreateAiImages}
                      onRegenerate={(request) => onGenerateSlideStructure(request)}
                      onConfirm={onConfirmSlideStructure}
                      onDismiss={() => setSlideStructureData(null)}
                    />
                  )}
                  {uploadProgress && <div className="px-1"><UploadProgress progress={uploadProgress} /></div>}
                  {isSelecting && (
                    <div className="pt-3 px-1">
                      <LoadingState label="Selecting a Custom GPT" />
                    </div>
                  )}
                  {compactionStatus ? <ContextCompactionDivider active /> : null}
                  {!compactionStatus && !isSelecting && !isSummarizing && showLoadingMessage && (
                    <Answer
                      answer={{ answer: "Generating response...", citations: [] }}
                      status={processMessages}
                      resubmitUserMessage={NOOP}
                      latest={false}
                      convDisabled={false}
                      terminateFileUpload={NOOP}
                    />
                  )}
                  {userMessageScrollSpacerHeight > 0 && <div style={{ height: userMessageScrollSpacerHeight }} />}
                  <div ref={chatMessageStreamEnd} />
                </div>
              </div>
              <ChatScrollNav
                messages={renderedMessages}
                scrollContainerRef={chatScrollContainer}
                showSenderName
              />
            </div>

            {/* Input */}
            <div className="max-w-screen-lg child-div flex flex-col shrink-0 w-full items-center justify-center pt-1">
              <ConversationFilesPanel
                files={appStateContext?.state.individualFileChat || []}
                open={!!appStateContext?.state.convFilesOpen}
                onOpenFile={onOpenFile}
                onClose={() => appStateContext?.dispatch({ type: 'SET_CONV_FILES_OPEN', payload: false })}
              />
              <div className="w-full">
                <PromptInput
                  placeholder={currentCustomGPT ? "Chat with " + currentCustomGPT.name : (isImagePromptMode ? "Describe the image you want" : "Ask something")}
                  disabled={isLoading || indexing || resubmitting}
                  sendPrompt={(q) => sendPrompt(q)}
                  handleAbort={stopGenerating}
                  question={question}
                  setQuestion={setQuestion}
                  conversationId={conversationId}
                  imageSize={imageSize}
                  setImageSize={setImageSize}
                  imageStyle={imageStyle}
                  setImageStyle={setImageStyle}
                  marketingSearchLevel={marketingSearchLevel}
                  setMarketingSearchLevel={setMarketingSearchLevel}
                  longConversation={longConversation}
                  isGroupChat={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupChat;
